import { useCallback, useMemo } from "react";

import type { AutocompleteRenderInputParams } from "@mui/material";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import { AutoCompleteSingleSelect, renderInput } from "../../../pickers/AutoCompleteSingleSelect";
import type {
  FreeSoloAutoCompleteSingleSelectProps,
  DropdownOption,
} from "../../../pickers/AutoCompleteSingleSelect";

export type MetricRangeNamePickerProps = {
  value: FreeSoloAutoCompleteSingleSelectProps["value"];
  onChange: FreeSoloAutoCompleteSingleSelectProps["onChange"];
  onInputChange?: FreeSoloAutoCompleteSingleSelectProps["onInputChange"];
  onBlur?: FreeSoloAutoCompleteSingleSelectProps["onBlur"];
  id?: string;
  label?: string;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: string;
};

const MetricRangeNamePicker = ({
  value,
  onChange,
  onInputChange,
  onBlur,
  id = "metric-range-name-picker",
  label,
  required = false,
  disabled = false,
  error,
  helperText,
}: MetricRangeNamePickerProps) => {
  const { t } = useTranslation(["common"]);

  const options = useMemo<DropdownOption<{ sortOrder: number }>[]>(() => {
    return [
      {
        id: uuidv4(),
        displayName: t("common:metric_range_name_extremely_low"),
        order: 1,
      },
      {
        id: uuidv4(),
        displayName: t("common:metric_range_name_very_low"),
        order: 2,
      },
      {
        id: uuidv4(),
        displayName: t("common:metric_range_name_low"),
        order: 3,
      },
      {
        id: uuidv4(),
        displayName: t("common:metric_range_name_operating_range"),
        order: 4,
      },
      {
        id: uuidv4(),
        displayName: t("common:metric_range_name_high"),
        order: 5,
      },
      {
        id: uuidv4(),
        displayName: t("common:metric_range_name_very_high"),
        order: 6,
      },
      {
        id: uuidv4(),
        displayName: t("common:metric_range_name_extremely_high"),
        order: 7,
      },
    ];
  }, [t]);

  const renderInputCallback = useCallback(
    (props: AutocompleteRenderInputParams) =>
      renderInput(props, label ?? t("common:range_name"), { required, error, helperText }),
    [error, helperText, label, required, t],
  );

  return (
    <AutoCompleteSingleSelect
      id={id}
      value={value}
      onChange={onChange}
      onInputChange={onInputChange}
      onBlur={onBlur}
      options={options}
      renderInput={renderInputCallback}
      disabled={disabled}
      fullWidth
      sortKey="order"
      freeSolo
    />
  );
};

export default MetricRangeNamePicker;
